/* eslint-disable no-console */
import { useEffect, useMemo, useState } from 'react';
import { useMembers, useSpace } from '@ably/spaces/react';
import type { SpaceMember } from '@ably/spaces';

/**
 * Aggregates other members' Spaces locations into `Map<rfqItemId, Member[]>`.
 * Each row reads `focusMap.get(itemId)` and passes the members to its
 * ProductCard, which renders the inline avatar + name presence indicator.
 *
 * The Spaces SDK already re-renders `useMembers` on every `space.update`
 * (including location changes — `createMember()` reads
 * `message.data.locationUpdate.current`), but a `version` tick on the
 * `locations.subscribe('update', ...)` path makes the recompute robust to
 * any case where `others` is a stable reference across renders.
 *
 * Logs at info level so the receiving end is visible in DevTools when
 * debugging.
 */
export const useRowFocus = () => {
  const { space } = useSpace();
  const { others } = useMembers();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!space) return undefined;
    const handler = (update: unknown) => {
      const u = update as {
        member?: { profileData?: unknown; connectionId?: string };
        currentLocation?: unknown;
        previousLocation?: unknown;
      };
      console.info('[spaces] received location update', {
        from: u?.member?.profileData,
        connectionId: u?.member?.connectionId,
        currentLocation: u?.currentLocation,
        previousLocation: u?.previousLocation
      });
      setTick(t => t + 1);
    };
    space.locations.subscribe('update', handler);
    return () => {
      try {
        space.locations.unsubscribe('update', handler);
      } catch (err) {
        console.warn('[spaces] locations.unsubscribe failed', err);
      }
    };
  }, [space]);

  return useMemo(() => {
    const map = new Map<string, SpaceMember[]>();
    (others || []).forEach(member => {
      const loc = member.location as { rfqItemId?: string } | null;
      const itemId = loc?.rfqItemId;
      if (!itemId) return;
      const existing = map.get(itemId) || [];
      existing.push(member);
      map.set(itemId, existing);
    });
    if (map.size > 0) {
      console.info(
        '[spaces] row focus map',
        Array.from(map.entries()).map(([id, ms]) => [
          id,
          ms.map(m => m.clientId)
        ])
      );
    }
    return map;
    // `tick` recomputes when locations.subscribe fires, in case `others`
    // reference identity is stable across a location-only update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [others, tick]);
};
